/**
 * Salesman + company (SOCIETY) interface scaling.
 *
 * Applies to every surface those two roles work in — the project hub, the
 * create wizards, and the urban-analysis hub — which is why it lives here
 * beside `theme.ts` rather than inside any one feature.
 *
 * The commercial interface renders 15% larger than the architect / back-office chrome, for
 * accessibility. Rather than a CSS `zoom`/`transform` on the shell, the
 * enlargement is expressed twice, from the single {@link HUB_SCALE} factor:
 *
 * - **Those surfaces' own styles** — every dimension is authored through {@link px},
 *   which multiplies the design value on the way out.
 * - **Ant Design's widgets** — buttons, inputs, selects, modals, tables and the
 *   rest are sized from seed tokens we do not author, so they are scaled through
 *   the theme in `hubTheme.ts`, mounted by `HubScaleProvider`.
 *
 * Keeping both on one factor is what makes the result read as *one* larger
 * interface rather than oversized cards wrapped around base-size controls.
 *
 * ## Why px and not rem
 *
 * An earlier revision emitted `rem`, to have the hub also follow the reader's
 * browser font-size setting. That looked right and was wrong, because AntD's
 * half cannot follow suit — its tokens are px numbers — so the two halves only
 * agree when the root happens to be 16px. On a browser configured for a 12px
 * default (Chrome's "Small"), `rem` resolved at 0.75×: the hub's own styles
 * rendered a quarter *smaller* than their design values while every AntD widget
 * beside them stayed at full size.
 *
 * px keeps the two halves in lockstep on any browser. Nothing is lost that the
 * app offered elsewhere — every other surface is authored in px too — and
 * browser *zoom*, which is what most readers actually reach for, scales px
 * exactly as it scales rem.
 */

/** How much larger the commercial interface renders than the rest of the app. */
export const HUB_SCALE = 1.15

/**
 * Convert a design pixel value to the CSS length the hub renders it at.
 *
 * @example
 *   px(16) // => '18.4px'
 *   px(20) // => '23px'
 *   px(0)  // => '0px'
 */
export const px = (value: number): string => `${scalePx(value)}px`

/**
 * The same conversion as {@link px}, kept as a *number* — Ant Design's theme
 * tokens are typed numeric and never accept a CSS string.
 *
 * Rounded to 2 decimals: the design values we scale are small and mostly
 * integral, so this is exact at every call site today and simply keeps a stray
 * float from leaking into generated CSS.
 */
export const scalePx = (value: number): number =>
  parseFloat((value * HUB_SCALE).toFixed(2))

/**
 * Size a square inline `<svg>` glyph at hub scale.
 *
 * Returned as a `style` object rather than `width`/`height` attributes: an icon
 * left on presentation attributes keeps its base size while the text beside it
 * grows.
 *
 * @example <svg viewBox="0 0 24 24" style={iconSize(20)} />
 */
export const iconSize = (value: number): { width: string; height: string } => ({
  width: px(value),
  height: px(value),
})
